import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addBundleScanSubsetParent,
  fetchBundleScanSubset,
  removeBundleScanSubsetParent,
} from '../../services/bundleScanSubsetAPI';
import { getBundleParentSkus } from '../../services/SKUListAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { BundleScanSubsetEntry } from '../../schemas/apiResponse.schemas';
import { SKU_PREFIX } from '../../constants/app.constants';
import { normalizeSku } from '../../utils/normalizeSku';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import BundleAllowlistCard from './BundleAllowlistCard';
import {
  excludeAllowlistedSkus,
  orderAllowlistEntries,
  pinAllowlistSku,
  unpinAllowlistSku,
} from './bundleAllowlistOrder';
import '../labels/PrintBoxLabel.css';
import './BundleScanSubsetManage.css';

const BundleScanSubsetManage: React.FC = () => {
  const [entries, setEntries] = useState<BundleScanSubsetEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [parentSkus, setParentSkus] = useState<string[]>([]);
  const [parentSkusLoading, setParentSkusLoading] = useState<boolean>(true);
  const [parentSkuInput, setParentSkuInput] = useState<string>(SKU_PREFIX);
  const [acting, setActing] = useState<boolean>(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [sessionAddedSkus, setSessionAddedSkus] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const skuInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);

  const searchableParentSkus = useMemo(
    () =>
      excludeAllowlistedSkus(
        parentSkus,
        entries.map((entry) => entry.parentSku)
      ),
    [parentSkus, entries]
  );

  const {
    filteredSkus,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: parentSkuInput,
    allSkus: searchableParentSkus,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef: skuInputRef,
    autocompleteRef,
  });

  const parentSkuSet = useMemo(
    () => new Set(parentSkus.map((sku) => normalizeSku(sku))),
    [parentSkus]
  );

  const loadEntries = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchBundleScanSubset();
      setSessionAddedSkus([]);
      setEntries(list);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load bundle scan subset'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    let cancelled = false;
    const loadParents = async (): Promise<void> => {
      setParentSkusLoading(true);
      try {
        const skus = await getBundleParentSkus();
        if (!cancelled) {
          setParentSkus(skus);
        }
      } catch (err: unknown) {
        console.error('Failed to load bundle parent SKUs:', err);
        if (!cancelled) {
          setParentSkus([]);
        }
      } finally {
        if (!cancelled) {
          setParentSkusLoading(false);
        }
      }
    };
    void loadParents();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAcceptedParentSku = useCallback(
    (sku: string): boolean => parentSkuSet.has(normalizeSku(sku)),
    [parentSkuSet]
  );

  const handleSkuInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length < SKU_PREFIX.length) {
      setParentSkuInput(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      setParentSkuInput(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      setParentSkuInput(value.toUpperCase());
    }
  };

  const selectSku = (sku: string): void => {
    if (!isAcceptedParentSku(sku)) {
      setError(`SKU ${sku} is not a bundle parent`);
      setMessage(null);
      return;
    }
    skipAutocompleteOpenRef.current = true;
    setParentSkuInput(sku);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setError(null);
  };

  const handleSkuInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showAutocomplete || filteredSkus.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        void handleAdd();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredSkus.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSku(filteredSkus[selectedIndex]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void handleAdd();
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const handleAdd = async (): Promise<void> => {
    const sku = parentSkuInput.trim();
    if (!sku || sku.length <= SKU_PREFIX.length || acting) return;

    if (!isAcceptedParentSku(sku)) {
      setError('Only SKUs listed as bundle parents can be added');
      setMessage(null);
      return;
    }

    setActing(true);
    setError(null);
    setMessage(null);
    try {
      const entry = await addBundleScanSubsetParent(sku);
      setEntries((prev) => [...prev, entry]);
      setSessionAddedSkus((prev) => pinAllowlistSku(prev, entry.parentSku));
      skipAutocompleteOpenRef.current = true;
      setParentSkuInput(SKU_PREFIX);
      setShowAutocomplete(false);
      setMessage(`Added ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to add parent SKU'));
    } finally {
      setActing(false);
    }
  };

  const handleRemove = async (entry: BundleScanSubsetEntry): Promise<void> => {
    const confirmed = window.confirm(
      `Remove "${entry.parentSku}" from the bundle scan subset list?`
    );
    if (!confirmed) return;

    setRemovingId(entry.id);
    setError(null);
    setMessage(null);
    try {
      await removeBundleScanSubsetParent(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      setSessionAddedSkus((prev) => unpinAllowlistSku(prev, entry.parentSku));
      setMessage(`Removed ${entry.parentSku}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to remove parent SKU'));
    } finally {
      setRemovingId(null);
    }
  };

  const displayedEntries = useMemo(
    () =>
      orderAllowlistEntries({
        entries,
        getSku: (entry) => entry.parentSku,
        pinnedSkus: sessionAddedSkus,
      }),
    [entries, sessionAddedSkus]
  );

  const canAdd =
    !acting &&
    !parentSkusLoading &&
    parentSkuInput.trim().length > SKU_PREFIX.length &&
    isAcceptedParentSku(parentSkuInput);

  return (
    <div className="pbl-container bssm-container">
      <p className="bssm-help">
        Parent bundles on this list use exception scan logic when no unscanned parent row
        exists on the order: components are marked from a single source (top-level or one
        other parent).
      </p>

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Add Parent Bundle SKU</span>
        </div>
        <div className="bssm-add-row">
          <div className="bssm-sku-finder">
            <SkuAutocompleteInput
              inputRef={skuInputRef}
              autocompleteRef={autocompleteRef}
              value={parentSkuInput}
              onChange={handleSkuInputChange}
              onKeyDown={handleSkuInputKeyDown}
              placeholder={`${SKU_PREFIX}...`}
              showAutocomplete={showAutocomplete}
              filteredSkus={filteredSkus}
              selectedIndex={selectedIndex}
              onSelectSku={selectSku}
              onHoverIndex={setSelectedIndex}
            />
          </div>
          <button
            type="button"
            className="bssm-add-btn"
            onClick={() => void handleAdd()}
            disabled={!canAdd}
          >
            {acting ? 'Adding...' : 'Add'}
          </button>
        </div>
        {parentSkusLoading && (
          <div className="pbl-message">Loading bundle parent SKUs...</div>
        )}
      </div>

      {error && <div className="bssm-banner bssm-banner--error">{error}</div>}
      {message && <div className="bssm-banner bssm-banner--ok">{message}</div>}

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">
            Allowlisted Parents{loading ? '' : ` (${entries.length})`}
          </span>
        </div>
        {loading ? (
          <div className="pbl-message">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="pbl-message">No parent bundles in the subset list.</div>
        ) : (
          <ul className="bssm-list">
            {displayedEntries.map((entry) => (
              <BundleAllowlistCard
                key={entry.id}
                parentSku={entry.parentSku}
                components={entry.components}
                removeDisabled={removingId === entry.id}
                removeLabel={removingId === entry.id ? 'Removing...' : 'Remove'}
                onRemove={() => void handleRemove(entry)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BundleScanSubsetManage;
